'use client'

import * as SliderPrimitive from '@radix-ui/react-slider'
import { isArray, isNil } from 'lodash-es'
import {
  RefObject,
  useEffect,
  useState,
  type ComponentPropsWithoutRef,
} from 'react'

import { cn } from '@/shared/lib'

const DEFAULT_THUMB_COUNT = 1

type Props = Omit<
  ComponentPropsWithoutRef<typeof SliderPrimitive.Root>,
  'onChange'
> & {
  color?: 'primary' | 'secondary'
  ref?: RefObject<HTMLSpanElement>
  format?: (value: number) => string
  showThumbLabel?: boolean
  showRange?: boolean
  options?: number[]
}

const mapValueToOptionIndex = (value: number, options: number[]) => {
  return options.findIndex(option => option === value)
}

export const Slider = ({
  defaultValue: defaultValueProp,
  value: valueProp,
  color = 'primary',
  ref,
  className,
  format,
  showThumbLabel = false,
  showRange = false,
  onValueChange,
  min,
  max,
  options,
  ...props
}: Props) => {
  const [localValue, setLocalValue] = useState(() => {
    if (isNil(options)) {
      return defaultValueProp ?? valueProp
    }

    if (!isNil(defaultValueProp)) {
      return defaultValueProp?.map(defaultValue =>
        mapValueToOptionIndex(defaultValue, options),
      )
    }

    return valueProp?.map(value => mapValueToOptionIndex(value, options))
  })

  const thumbCount = (() => {
    if (isArray(defaultValueProp)) {
      return defaultValueProp.length
    }

    if (isArray(valueProp)) {
      return valueProp.length
    }

    return DEFAULT_THUMB_COUNT
  })()

  const range = (() => {
    if (isArray(options)) {
      return {
        min: options[0] ?? 0,
        max: options.at(-1) ?? 0,
      }
    }

    return { min: min ?? 0, max: max ?? 0 }
  })()

  const getLabelText = (index: number) => {
    if (options) {
      const optionIndex = localValue?.[index] ?? 0

      return options[optionIndex]
    }

    return localValue?.[index] ?? 0
  }

  const handleValueChange = (value: number[]) => {
    const mappedValue = options ? value.map(value => options[value]) : value

    setLocalValue(value)
    onValueChange?.(mappedValue)
  }

  useEffect(() => {
    const mappedValue = options
      ? valueProp?.map(value => mapValueToOptionIndex(value, options))
      : valueProp

    setLocalValue(mappedValue)
  }, [valueProp])

  return (
    <div
      className={cn('relative flex h-11', {
        'mb-[18px]': showThumbLabel || showRange,
      })}
    >
      {showRange && (
        <div className="typo-caption1 absolute top-full left-0 flex w-full justify-between font-medium text-gray-500">
          <p>{typeof format === 'function' ? format(range.min) : range.min}</p>
          <p>{typeof format === 'function' ? format(range.max) : range.max}</p>
        </div>
      )}
      <SliderPrimitive.Root
        ref={ref}
        value={localValue}
        onValueChange={handleValueChange}
        className={cn(
          'relative flex w-full touch-none items-center select-none',
          className,
        )}
        min={min}
        max={max}
        {...props}
        {...(options && {
          min: 0,
          max: options.length - 1,
          step: 1,
        })}
      >
        <SliderPrimitive.Track className="relative h-1 w-full grow overflow-hidden rounded-full bg-gray-200">
          <SliderPrimitive.Range
            className={cn('absolute h-full', {
              'bg-black': color === 'primary',
              'bg-purple-500': color === 'secondary',
            })}
          />
        </SliderPrimitive.Track>
        {Array.from({ length: thumbCount }).map((_, index) => (
          <SliderPrimitive.Thumb
            key={index}
            className={cn(
              'block h-6 w-6 rounded-full bg-white transition-all',
              'shadow-[0px_5.14px_11.14px_0px_rgba(0,0,0,0.12),0px_0.43px_3.43px_0px_rgba(0,0,0,0.12)]',
              'focus-visible:ring-3 focus-visible:outline-none', // FIXME: 포커스 스타일 고민
              'disabled:pointer-events-none disabled:opacity-50',
              {
                'focus-visible:ring-ring/50': color === 'primary',
                'focus-visible:ring-purple-200/50': color === 'secondary',
              },
            )}
          >
            {(() => {
              if (!showThumbLabel) {
                return null
              }

              const labelText = getLabelText(index)

              return (
                <span className="typo-caption1 absolute top-[calc(100%+10px)] left-1/2 w-max -translate-x-1/2 font-medium text-black">
                  {typeof format === 'function' ? format(labelText) : labelText}
                </span>
              )
            })()}
          </SliderPrimitive.Thumb>
        ))}
      </SliderPrimitive.Root>
    </div>
  )
}
